import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@/stores/authStore';

let socketInstance: Socket | null = null;
let socketRefCount = 0;

function getSocket(): Socket {
  if (!socketInstance) {
    socketInstance = io(window.location.origin, {
      path: '/ws',
      transports: ['websocket', 'polling'],
      withCredentials: true,
      autoConnect: false,
    });
  }
  return socketInstance;
}

/**
 * Hook to access the shared WebSocket connection.
 * Auto-connects when user is authenticated, disconnects on logout.
 */
export function useSocket(): { socket: Socket | null; connected: boolean } {
  const user = useAuthStore((s) => s.user);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!user) {
      // User logged out — disconnect if we have a socket
      if (socketInstance?.connected) {
        socketInstance.disconnect();
      }
      setConnected(false);
      return;
    }

    const socket = getSocket();
    socketRef.current = socket;
    socketRefCount++;

    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    if (!socket.connected) {
      socket.connect();
    } else {
      setConnected(true);
    }

    socket.on('connect', () => {
      // v1.1.15 fix — multi-org users have multiple `organization_members`
      // rows; the server's auto-join does `organization_members.first()`
      // with no ORDER BY, so it picks an arbitrary org. The client knows
      // which org the user is actually working in (from the active-org
      // localStorage key) — tell the server explicitly so the socket
      // joins the RIGHT org room and receives this org's events.
      try {
        const orgKey = Object.keys(localStorage).find(k => k.endsWith('active_org_id'));
        const activeOrgId = orgKey ? localStorage.getItem(orgKey) : null;
        if (activeOrgId) {
          socket.emit('join:org', activeOrgId);
        }
      } catch {
        /* ignore — server-side auto-join is the fallback */
      }
    });

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socketRefCount--;

      if (socketRefCount <= 0 && socketInstance) {
        socketInstance.disconnect();
        socketInstance = null;
        socketRefCount = 0;
      }
    };
  }, [user]);

  return { socket: socketRef.current, connected };
}

/**
 * Hook to subscribe to a specific socket event with auto-cleanup.
 */
export function useSocketEvent<T = any>(
  event: string,
  handler: (data: T) => void,
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    // v1.1.13 fix — call getSocket() to ensure the socket instance exists
    // before subscribing. Pre-fix this read `socketInstance` directly and
    // bailed out if null. React runs child effects BEFORE parent effects,
    // so useSocketEvent (deep in the tree) ran before useSocket() (in
    // BaseLayout) created the socket. The early-return meant the listener
    // was never registered — even though the socket came online a moment
    // later, events fired against zero listeners. socket.io supports
    // attaching listeners pre-connect; events are dispatched once it
    // connects, so this is safe.
    const socket = getSocket();

    const wrappedHandler = (data: T) => handlerRef.current(data);
    socket.on(event, wrappedHandler);

    return () => {
      socket.off(event, wrappedHandler);
    };
  }, [event]);
}
